import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useSnackbar } from '@/context/SnackbarProvider';
import {
  AdminProperty,
  deleteProperty,
  deleteReport,
  transferProperty,
  triggerScoreRecalculation,
} from '@/services/api/admin';
import { getAllProperties, updatePropertyHasHOA } from '@/services/api/properties/api';

export const ADMIN_PROPERTIES_QUERY_KEY = ['admin', 'properties'];

// GET /properties/all is server-paginated (see getAllProperties.ts) to bound
// per-request enrichment work — a full-table scan + N+1 fan-out on every
// call was the original resilience-audit finding here. The 4 consumers of
// this hook (PropertiesTab, PropertySelector, AdvisorClientsPage,
// PropertiesPageNew) all still expect the complete flat array today —
// client-side search, in-memory id lookups, and admin stat tiles are built
// on it — so rather than rewriting all 4 to page through results by hand,
// this hook pages through them itself in the background and exposes the
// same flattened `properties` shape those consumers already use. The real
// fix this unlocks either way: each individual request is now bounded to
// one page's worth of scan/enrichment work instead of the whole table, so a
// slow/large account can no longer produce one massive blocking Lambda call.
export const useAdminProperties = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const query = useInfiniteQuery({
    queryKey: ADMIN_PROPERTIES_QUERY_KEY,
    queryFn: ({ pageParam }: { pageParam?: string }) => getAllProperties({ cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30 * 1000, // 30 seconds
    enabled,
  });

  // Auto-advance through every page as soon as it's available — consumers
  // get the full list eventually, same as the old single-request behavior,
  // just via several bounded requests instead of one unbounded one.
  useEffect(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage();
    }
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  return {
    properties: query.data?.pages.flatMap((page) => page.properties) ?? [],
    isLoading: query.isLoading,
    // Only "fetching" in the consumer-visible sense while the first page is
    // in flight or a manual refetch is running — background page-2-onward
    // fetches shouldn't flip every consumer's loading spinner back on.
    isFetching: query.isFetching && !query.isFetchingNextPage,
    // True from mount until every page has landed — covers the initial
    // (first-page) load too, not just page 2 onward, so "do I have the
    // complete list yet" is always just `!isFetchingAllPages`. Consumers
    // that want to show partial data + a subtle "loading more" affordance
    // instead can use `isFetching` (first-page-only) alongside this.
    isFetchingAllPages: query.isLoading || query.hasNextPage === true || query.isFetchingNextPage,
    error: query.error,
    refetch: query.refetch,
  };
};

// Real, single-page-at-a-time pagination + server-side search — unlike
// useAdminProperties above, this deliberately does NOT auto-fetch every
// page. It exists for the main Properties list/search UI, where "only ever
// hold one page in memory, and let search narrow what the backend scans"
// is the actual goal (see getAllProperties.ts's `search` param — a bounded
// scan-and-filter, not a client-side filter over an already-fully-fetched
// list). Search terms reset pagination automatically since `search` is
// part of the query key. Other consumers (buyer-view enrichment, selectors,
// KPI totals that need the whole table) should keep using
// useAdminProperties, not this hook.
export const useAdminPropertiesSearch = ({
  search = '',
  enabled = true,
}: { search?: string; enabled?: boolean } = {}) => {
  const query = useInfiniteQuery({
    queryKey: [...ADMIN_PROPERTIES_QUERY_KEY, 'search', search],
    queryFn: ({ pageParam }: { pageParam?: string }) =>
      getAllProperties({ cursor: pageParam, search: search || undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30 * 1000,
    enabled,
  });

  return {
    properties: query.data?.pages.flatMap((page) => page.properties) ?? [],
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useDeleteProperty = () => {
  const queryClient = useQueryClient();
  const { showAlert } = useSnackbar();

  return useMutation({
    mutationFn: (propertyId: string) => deleteProperty(propertyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
      showAlert('Property deleted', 'success');
    },
    onError: () => {
      showAlert('Failed to delete property', 'error');
    },
  });
};

export const useDeleteReport = () => {
  const queryClient = useQueryClient();
  const { showAlert } = useSnackbar();

  return useMutation({
    mutationFn: ({
      propertyId,
      reportId,
      documentRecordId,
    }: {
      propertyId: string;
      reportId: string;
      documentRecordId?: string;
    }) => deleteReport(propertyId, reportId, documentRecordId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
      showAlert('Inspection report deleted', 'success');
    },
    onError: () => {
      showAlert('Failed to delete inspection report', 'error');
    },
  });
};

export const useTriggerRecalculation = () => {
  const { showAlert } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ propertyId, scoreType }: { propertyId: string; scoreType: 'home' | 'area' }) =>
      triggerScoreRecalculation(propertyId, scoreType),
    onSuccess: (_data, { propertyId, scoreType }) => {
      queryClient.invalidateQueries({ queryKey: ['hauser-eq-results', propertyId] });
      showAlert(
        `${scoreType === 'home' ? 'Home' : 'Area'} score recalculation queued — may take a few minutes`,
        'info',
      );
    },
    onError: (_error, { scoreType }) => {
      showAlert(
        `Failed to queue ${scoreType === 'home' ? 'Home' : 'Area'} score recalculation`,
        'error',
      );
    },
  });
};

export const useTransferProperty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      propertyId,
      toUserEmail,
      documentRecordIds,
    }: {
      propertyId: string;
      toUserEmail: string;
      documentRecordIds: string[];
    }) => transferProperty(propertyId, toUserEmail, documentRecordIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
    },
  });
};

export const useUpdateHasHOA = () => {
  const queryClient = useQueryClient();
  const { showAlert } = useSnackbar();

  return useMutation({
    mutationFn: ({ propertyId, hasHOA }: { propertyId: string; hasHOA: boolean }) =>
      updatePropertyHasHOA(propertyId, hasHOA),
    onSuccess: (_data, { propertyId }) => {
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
      // Separate cache from the one above — HOAChecklistCard/HOADetailView/the
      // sidebar nav all read this key directly and won't otherwise notice the
      // toggle until their 5-minute staleTime expires.
      queryClient.invalidateQueries({ queryKey: ['hoa-checklist', propertyId] });
    },
    onError: () => {
      showAlert('Failed to update HOA status', 'error');
    },
  });
};

export type { AdminProperty };
